import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { Col, FlashMessage, InputSelect, InputText, Row } from '../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../hooks';
import { useVatNumberValidator } from '../../../../hooks/useVatNumberValidator';
import { useDeferredVatEligibility } from '../../../../hooks/useDeferredVatEligibility';
import { translate } from '../../../../utils/localization';
import { validateDutchPostalCode, validateLine1, validateRequired } from '../../../../utils/validation';
import { actions } from '../../ducks';
import { CompanyDetails } from '../../typings';
import { normalizeVatNumber } from '../../../../utils/normalisation';
import { CountryCode } from '@brenger/api-client';

interface Props {
  forceShowErrors: boolean;
}

export const BusinessFields: React.FC<Props> = ({ forceShowErrors }) => {
  const dispatch = useDispatch();
  const details = useTypedSelector(state => state.generalTransport.contact?.customer?.company_details);
  const isLoggedIn = useTypedSelector(state => state.user.loggedIn);

  const { isVatLoading, isVatValid } = useVatNumberValidator(details?.vat_number || '');
  // Logged-in users get this from their account instead
  useDeferredVatEligibility(
    details?.vat_number || '',
    (details?.address?.country_code as CountryCode) || 'NL',
    !isLoggedIn && isVatValid && !!details?.address?.country_code
  );

  const save = (newDetails: Partial<CompanyDetails>): void => {
    dispatch(
      actions.setCompanyDetails({
        ...details,
        ...newDetails,
        address: {
          country_code: 'NL', // Works as default, will be overwritten by selection or existing
          ...(details?.address || {}),
          ...(newDetails?.address || {}),
        },
      } as Partial<CompanyDetails>)
    );
  };

  // Supported countries are based on supported languages + BE
  const countryOptions = [
    {
      label: 'NL',
      value: 'NL',
    },
    {
      label: 'BE',
      value: 'BE',
    },
    {
      label: 'DE',
      value: 'DE',
    },
  ];

  return (
    <>
      <Row>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            placeholder={translate('form.fields.account.account_name.label')}
            value={details?.company_name || ''}
            onChange={company_name => save({ company_name })}
            meta={{
              error: validateRequired(translate('form.fields.account.account_name.label'), details?.company_name),
              showError: forceShowErrors,
            }}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            placeholder={translate('form.fields.account.coc_number.label')}
            value={details?.coc_number || ''}
            onChange={coc_number => save({ coc_number })}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            placeholder={translate('form.fields.account.vat_number.label')}
            value={details?.vat_number || ''}
            onChange={vat_number => save({ vat_number: normalizeVatNumber(vat_number) })}
            isLoading={isVatLoading}
            meta={{
              error: details?.vat_number && !isVatValid ? translate('form.fields.account.vat_number.valid') : false,
              showError: forceShowErrors,
            }}
          />
          {isVatValid && (
            <>
              <div className="pb-[0.5em]" />
              <FlashMessage type="info">
                <Translate id="form.fields.account.vat_number.validated" />
              </FlashMessage>
            </>
          )}
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            placeholder={translate('form.fields.address.line1.label')}
            value={details?.address?.line1 || ''}
            onChange={line1 => save({ address: { line1 } } as Partial<CompanyDetails>)}
            meta={{ error: validateLine1(details?.address?.line1), showError: forceShowErrors }}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            placeholder={translate('form.fields.address.postal_code.label')}
            value={details?.address?.postal_code || ''}
            onChange={postal_code => save({ address: { postal_code } } as Partial<CompanyDetails>)}
            meta={{
              error:
                details?.address?.country_code === 'NL'
                  ? validateDutchPostalCode(details?.address?.postal_code)
                  : validateRequired(translate('form.fields.address.postal_code.label'), details?.address?.postal_code),
              showError: forceShowErrors,
            }}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputText
            placeholder={translate('form.fields.address.locality.label')}
            value={details?.address?.locality || ''}
            onChange={locality => save({ address: { locality } } as Partial<CompanyDetails>)}
            meta={{
              error: validateRequired(translate('form.fields.address.locality.label'), details?.address?.locality),
              showError: forceShowErrors,
            }}
          />
        </Col>
        <Col xs={12} extraClasses="pb-[1em]">
          <InputSelect
            id={'business_country'}
            placeholder={translate('form.fields.address.country.label')}
            value={details?.address?.country_code}
            onChange={country_code => save({ address: { country_code } } as Partial<CompanyDetails>)}
            addEmptyOption={false}
            options={countryOptions}
            meta={{
              error: validateRequired(
                translate('form.fields.address.country_code.label'),
                details?.address?.country_code
              ),
              showError: forceShowErrors,
            }}
          />
        </Col>
      </Row>
      <div style={{ paddingBottom: '2rem' }} />
    </>
  );
};
